import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const PaybackPeriod = () => {
  // State variables for Payback Period calculation
  const [initialInvestment, setInitialInvestment] = useState(0); // Initial investment
  const [annualCashInflow, setAnnualCashInflow] = useState(0); // Annual cash inflow
  const [paybackPeriod, setPaybackPeriod] = useState(0); // Calculated payback period

  // Event handlers to update state values
  const handleInvestmentChange = (e) =>
    setInitialInvestment(parseFloat(e.target.value) || 0);
  const handleCashInflowChange = (e) =>
    setAnnualCashInflow(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Payback Period
  useEffect(() => {
    if (annualCashInflow > 0) {
      // Payback Period = Initial Investment / Annual Cash Inflow
      const payback = initialInvestment / annualCashInflow;
      setPaybackPeriod(payback);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [initialInvestment, annualCashInflow]);

  return (
    <Layout title="Project Payback Period Calculator" path="payback-period">
      <section className="section-padding" id="payback-period">
        <div className="container mt-5">
          <div className="row">
            {/* Payback Period Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Project Payback Period Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Initial Investment */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="initialInvestment" className="form-label">
                        Initial Investment
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Initial Investment:</strong> The total amount of money invested at the start of the project."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="initialInvestment"
                        placeholder="Enter initial investment amount"
                        value={initialInvestment || ""}
                        onChange={handleInvestmentChange}
                      />
                    </div>

                    {/* Input for Annual Cash Inflow */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualCashInflow" className="form-label">
                        Annual Cash Inflow
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Cash Inflow:</strong> The amount of money generated by the project each year."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualCashInflow"
                        placeholder="Enter annual cash inflow"
                        value={annualCashInflow || ""}
                        onChange={handleCashInflowChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Payback Period:{" "}
                        <span className="fw-bold ms-3">
                          {paybackPeriod === 0
                            ? "0"
                            : paybackPeriod.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          years
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Project Payback Period
                </h3>

                {/* What is Payback Period? */}
                <h4 className="mt-3 mb-3">What is Payback Period?</h4>
                <p>
                  The **Payback Period** is the time it takes for an investment
                  to recover its initial cost from the cash inflows it
                  generates. It is a simple yet effective metric used to
                  evaluate project feasibility and the risk associated with
                  investments.
                </p>

                {/* Why Use the Payback Period? */}
                <h4 className="mt-4 mb-3">Why Use the Payback Period?</h4>
                <p>
                  The payback period helps businesses assess how quickly they
                  can expect to recoup their investment, which is crucial for
                  decision-making, especially in projects with limited budgets
                  or high uncertainty. A shorter payback period typically
                  indicates a lower risk.
                </p>

                {/* Benefits of Using the Payback Period */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Payback Period
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Businesses Assess Project Feasibility:
                    </strong>{" "}
                    By calculating the payback period, businesses can determine
                    if a project is financially viable.
                  </li>
                  <li>
                    <strong>Supports Financial Decision-Making:</strong> It
                    enables decision-makers to quickly compare different
                    projects or investment options based on the speed of return.
                  </li>
                  <li>
                    <strong>Useful for Comparing Investment Options:</strong>{" "}
                    The payback period provides a simple and quick way to assess
                    multiple investment opportunities and prioritize them.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Payback Period?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Project Management Teams:</strong> Helps project
                    managers assess the financial viability of projects.
                  </li>
                  <li>
                    <strong>Corporate Finance Teams:</strong> Assists in
                    financial planning and investment decisions within an
                    organization.
                  </li>
                  <li>
                    <strong>Investors:</strong> Provides insights into the
                    potential return on investment and project risks.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is a good payback period for an investment?
                </h4>
                <p>
                  A good payback period varies by industry and investment type.
                  Generally, a shorter payback period is preferred as it
                  indicates quicker return on investment, reducing the financial
                  risk. However, the optimal payback period also depends on the
                  specific goals and risk tolerance of the business or investor.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is the Payback Period important for financial planning?
                </h4>
                <p>
                  The payback period is important for financial planning because
                  it helps businesses and investors assess the risk and
                  liquidity of a project. By understanding how long it will take
                  to recover an initial investment, companies can ensure that
                  they have enough resources to continue operations and make
                  informed investment choices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PaybackPeriod;
